import { Link } from 'react-router-dom'

import { useFeed } from '../hooks/queries'
import type { FeedPull } from '../lib/api'
import { cents, quantity, timeAgo } from '../lib/format'
import { AssetLogo } from './AssetLogo'
import { Avatar } from './Avatar'
import { KindBadge, RarityChip, Skeleton, rarityClass } from './ui'

/**
 * The same live feed, compressed into a single-line pill for the header —
 * every page, not just the home page, and always visible rather than
 * something you scroll past once. Deliberately terser than `PulledCard`:
 * logo, symbol and value only, no avatar or handle, because a header strip
 * competes for room with the nav and the balance on every page it appears on.
 * Shares the same doubled-track marquee mechanic as the home page version.
 */
export function HeaderTicker() {
  const { data: pulls } = useFeed(20)
  if (!pulls || pulls.length === 0) return null

  return (
    <div className="marquee relative w-full overflow-hidden">
      <div className="marquee-track gap-2">
        {pulls.map((pull) => (
          <TickerPill key={pull.id} pull={pull} />
        ))}
        {pulls.map((pull) => (
          <TickerPill key={`echo-${pull.id}`} pull={pull} aria-hidden />
        ))}
      </div>

      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-8"
        style={{ background: 'linear-gradient(to right, var(--color-bg), transparent)' }}
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-8"
        style={{ background: 'linear-gradient(to left, var(--color-bg), transparent)' }}
      />
    </div>
  )
}

function TickerPill({ pull, ...rest }: { pull: FeedPull } & React.HTMLAttributes<HTMLAnchorElement>) {
  return (
    <Link
      to={`/pack/${pull.packId}`}
      className={`${rarityClass(pull.rarity)} flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs transition-colors hover:brightness-125`}
      style={{
        background: 'color-mix(in srgb, var(--rarity) 10%, transparent)',
        border: '1px solid color-mix(in srgb, var(--rarity) 26%, transparent)',
      }}
      {...rest}
    >
      <AssetLogo kind={pull.kind} symbol={pull.symbol} rarity={pull.rarity} size={16} />
      <span className="font-semibold">{pull.symbol}</span>
      <span className="tnum" style={{ color: 'var(--color-muted)' }}>
        {cents(pull.valueCents)}
      </span>
    </Link>
  )
}

/**
 * "Just Pulled" — the live strip along the top of the home page.
 *
 * A CSS marquee over a doubled track rather than a scroll-position animation:
 * it runs on the compositor, costs nothing per frame, and pauses on hover so a
 * card that catches someone's eye can actually be read and clicked. It also
 * stops dead under `prefers-reduced-motion`, where it becomes a normal
 * horizontally-scrollable row.
 */
export function JustPulled() {
  const { data: pulls, isLoading } = useFeed(30)

  if (isLoading) {
    return (
      <div className="flex gap-3 overflow-hidden py-1">
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton key={i} className="h-[68px] w-[230px] shrink-0" />
        ))}
      </div>
    )
  }

  if (!pulls || pulls.length === 0) return null

  // Two copies so the -50% keyframe lands exactly on the seam and the loop is
  // invisible. `aria-hidden` on the duplicate keeps it out of the a11y tree.
  return (
    <div className="marquee no-scrollbar relative overflow-x-auto lg:overflow-hidden">
      <div className="marquee-track gap-3 py-1">
        {pulls.map((pull) => (
          <PulledCard key={pull.id} pull={pull} />
        ))}
        {pulls.map((pull) => (
          <PulledCard key={`echo-${pull.id}`} pull={pull} aria-hidden />
        ))}
      </div>

      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-12"
        style={{ background: 'linear-gradient(to right, var(--color-bg), transparent)' }}
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-12"
        style={{ background: 'linear-gradient(to left, var(--color-bg), transparent)' }}
      />
    </div>
  )
}

function PulledCard({ pull, ...rest }: { pull: FeedPull } & React.HTMLAttributes<HTMLAnchorElement>) {
  return (
    <Link
      to={`/pack/${pull.packId}`}
      className={`panel-2 ${rarityClass(pull.rarity)} flex w-[250px] shrink-0 items-center gap-3 px-3 py-2.5 transition-colors hover:brightness-125`}
      style={{ borderColor: 'color-mix(in srgb, var(--rarity) 28%, var(--color-line))' }}
      {...rest}
    >
      <AssetLogo kind={pull.kind} symbol={pull.symbol} rarity={pull.rarity} size={36} />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-xs font-semibold">{pull.symbol}</span>
          <KindBadge kind={pull.kind} />
        </div>
        <div className="tnum mt-0.5 truncate text-[0.6875rem]" style={{ color: 'var(--color-muted)' }}>
          {quantity(pull.quantity)} · {cents(pull.valueCents)}
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <RarityChip rarity={pull.rarity} />
          {/* Who pulled it, not a link: the whole card is already a link to the
              pack, and a nested anchor is invalid HTML. The handle is here to
              give the result a face; the profile is one hop away from the
              leaderboard. */}
          <Avatar identity={pull.by} size={14} />
          <span className="truncate text-[0.625rem]" style={{ color: 'var(--color-faint)' }}>
            {pull.by.handle} · {timeAgo(pull.openedAt)}
          </span>
        </div>
      </div>
    </Link>
  )
}
